import React, { useState, useEffect } from 'react';
import '../../components/styles/SignUp.css';
import '../../components/styles/SignUpPage.css';
import Button from '../Button';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const steps = [
  'Applicant Information',
  'Family Information',
  'Emergency Contact Information',
  'Student Educational Background',
  'Academic & Personal Interests',
  'Additional Information',
  'Technology Access',
  'Transcript Upload',
  'Important Information',
  'Signature & Acknowledgment',
];

const ParentApplicationPage = ({ step, setStep }) => {
  const [form, setForm] = useState({
    // Step 1: Applicant Information
    studentFullName: '',
    studentPreferredName: '',
    studentDOB: '',
    studentCurrentGrade: '',
    studentApplyingGrade: '',
    studentEmail: '',
    studentPhone: '',
    
    // Step 2: Family Information
    parent1FullName: '',
    parent1Relationship: '',
    parent1Email: '',
    parent1Phone: '',
    parent1Address: '',
    parent1Occupation: '',
    parent1Employer: '',
    parent2FullName: '',
    parent2Relationship: '',
    parent2Email: '',
    parent2Phone: '',
    parent2Address: '',
    parent2Occupation: '',
    parent2Employer: '',
    
    // Step 3: Emergency Contact Information
    emergencyName: '',
    emergencyRelationship: '',
    emergencyPrimaryPhone: '',
    emergencyAlternatePhone: '',
    emergencySameAsParent1: false,
    
    // Step 4: Student Educational Background
    schoolName: '',
    schoolDates: '',
    schoolReason: '',
    
    // Step 5: Academic & Personal Interests
    academicStrengths: '',
    hobbies: '',
    interestInDual: '',
    
    // Step 6: Additional Information
    specialNeeds: '',
    
    // Step 7: Technology Access
    techComputer: false,
    techInternet: false,
    // Step 8: Transcript Upload
    transcriptFiles: [],
    
    // Step 10: Signature & Acknowledgment
    parentSignature: '',
    studentSignature: '',
    parentSignatureDate: '',
    studentSignatureDate: '',
  });
  const [isComplete, setIsComplete] = useState(false);
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
  const navigate = useNavigate();

  // Add useEffect to load saved application data and step on mount
  useEffect(() => {
    const fetchApplication = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${API_ENDPOINT}/api/application/`, {
          headers: { Authorization: `Token ${token}` },
        });
        const app = res.data;
        if (app && app.application_status === 'complete') {
          setIsComplete(true);
          setStep(0); // Always start at step 1 if complete
        }
        if (app && app.form_data && Array.isArray(app.form_data)) {
          // Flatten form_data array into a single object for form state
          const newForm = { ...form };
          let firstIncompleteStep = app.form_data.length - 1;
          let anyFilled = false;
          app.form_data.forEach((stepObj, idx) => {
            if (stepObj && stepObj.data) {
              Object.entries(stepObj.data).forEach(([k, v]) => {
                newForm[k] = v;
              });
              // If all fields in this step are empty/default, mark as first incomplete
              const allEmpty = Object.values(stepObj.data).every(
                (v) => v === '' || v === false || v === null || (Array.isArray(v) && v.length === 0)
              );
              if (!allEmpty) anyFilled = true;
              if (allEmpty && firstIncompleteStep === app.form_data.length - 1) {
                firstIncompleteStep = idx;
              }
            }
          });
          setForm(newForm);
          setStep(anyFilled ? firstIncompleteStep : 0);
        }
      } catch (err) {
        setStep(0);
      }
    };
    fetchApplication();
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const saveApplication = async (data, status) => {
    try {
      const token = localStorage.getItem('token');
      
      // Organize form data by steps to maintain logical order
      const organizedData = [
        {
          step: 1,
          title: "Applicant Information",
          data: {
            studentFullName: data.studentFullName,
            studentPreferredName: data.studentPreferredName,
            studentDOB: data.studentDOB,
            studentCurrentGrade: data.studentCurrentGrade,
            studentApplyingGrade: data.studentApplyingGrade,
            studentEmail: data.studentEmail,
            studentPhone: data.studentPhone,
          }
        },
        {
          step: 2,
          title: "Family Information",
          data: {
            parent1FullName: data.parent1FullName,
            parent1Relationship: data.parent1Relationship,
            parent1Email: data.parent1Email,
            parent1Phone: data.parent1Phone,
            parent1Address: data.parent1Address,
            parent1Occupation: data.parent1Occupation,
            parent1Employer: data.parent1Employer,
            parent2FullName: data.parent2FullName,
            parent2Relationship: data.parent2Relationship,
            parent2Email: data.parent2Email,
            parent2Phone: data.parent2Phone,
            parent2Address: data.parent2Address,
            parent2Occupation: data.parent2Occupation,
            parent2Employer: data.parent2Employer,
          }
        },
        {
          step: 3,
          title: "Emergency Contact Information",
          data: {
            emergencyName: data.emergencyName,
            emergencyRelationship: data.emergencyRelationship,
            emergencyPrimaryPhone: data.emergencyPrimaryPhone,
            emergencyAlternatePhone: data.emergencyAlternatePhone,
            emergencySameAsParent1: data.emergencySameAsParent1,
          }
        },
        {
          step: 4,
          title: "Student Educational Background",
          data: {
            schoolName: data.schoolName,
            schoolDates: data.schoolDates,
            schoolReason: data.schoolReason,
          }
        },
        {
          step: 5,
          title: "Academic & Personal Interests",
          data: {
            academicStrengths: data.academicStrengths,
            hobbies: data.hobbies,
            interestInDual: data.interestInDual,
          }
        },
        {
          step: 6,
          title: "Additional Information",
          data: {
            specialNeeds: data.specialNeeds,
          }
        },
        {
          step: 7,
          title: "Technology Access",
          data: {
            techComputer: data.techComputer,
            techInternet: data.techInternet,
          }
        },
        {
          step: 8,
          title: "Transcript Upload",
          data: {
            transcriptFiles: data.transcriptFiles,
          }
        },
        {
          step: 10,
          title: "Signature & Acknowledgment",
          data: {
            parentSignature: data.parentSignature,
            studentSignature: data.studentSignature,
            parentSignatureDate: data.parentSignatureDate,
            studentSignatureDate: data.studentSignatureDate,
          }
        }
      ];
      
      await axios.patch(
        `${API_ENDPOINT}/api/application/`,
        { form_data: organizedData, application_status: status },
        { headers: { Authorization: `Token ${token}` } }
      );
    } catch (err) {
      alert('Failed to save application. Please check your connection.');
    }
  };

  const handleNext = async (e) => {
    e && e.preventDefault();
    await saveApplication(form, 'in_progress');
    if (step < steps.length - 1) {
      setStep(step + 1);
      localStorage.setItem('applicationStep', (step + 1).toString());
    }
  };
  const handleBack = async (e) => {
    e && e.preventDefault();
    await saveApplication(form, 'in_progress');
    if (step > 0) {
      setStep(step - 1);
      localStorage.setItem('applicationStep', (step - 1).toString());
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveApplication(form, 'complete');
    // Re-fetch current user to update application_status
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_ENDPOINT}/api/current-user/`, {
        headers: { Authorization: `Token ${token}` },
      });
      localStorage.setItem('cachedUser', JSON.stringify(response.data));
      navigate('/parent-applications');
      // Reload the page after navigation to ensure all components update
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (err) {
      navigate('/parent-applications');
      // Reload the page after navigation to ensure all components update
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  // Add uploadedTranscripts state to track uploaded file names
  const [uploadedTranscripts, setUploadedTranscripts] = useState([]);

  // Handler for transcript upload
  const handleTranscriptUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const token = localStorage.getItem('token');
    const uploaded = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(
          `${API_ENDPOINT}/api/application/`,
          formData,
          {
            headers: {
              Authorization: `Token ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        uploaded.push(file.name);
      } catch (err) {
        alert(`Failed to upload ${file.name}`);
      }
    }
    setUploadedTranscripts((prev) => [...prev, ...uploaded]);
    setForm((prev) => ({ ...prev, transcriptFiles: uploaded }));
  };

  // Each step's JSX
  const stepContent = [
    // 1. Applicant Information
    <>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="form-control" style={{ flex: 1 }} name="studentFullName" value={form.studentFullName} onChange={handleChange} placeholder="Full Legal Name" required />
        <input className="form-control" style={{ flex: 1 }} name="studentPreferredName" value={form.studentPreferredName} onChange={handleChange} placeholder="Preferred Name" />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input className="form-control" style={{ flex: 1 }} name="studentDOB" value={form.studentDOB} onChange={handleChange} placeholder="Date of Birth (MM/DD/YYYY)" type="text" required />
        <input className="form-control" style={{ flex: 1 }} name="studentCurrentGrade" value={form.studentCurrentGrade} onChange={handleChange} placeholder="Current Grade Level" required />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input className="form-control" style={{ flex: 1 }} name="studentApplyingGrade" value={form.studentApplyingGrade} onChange={handleChange} placeholder="Grade Level Applying For (9, 10, 11, 12)" required />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input className="form-control" style={{ flex: 1 }} name="studentEmail" value={form.studentEmail} onChange={handleChange} placeholder="Applicant Email" required />
        <input className="form-control" style={{ flex: 1 }} name="studentPhone" value={form.studentPhone} onChange={handleChange} placeholder="Applicant Phone Number (optional)" />
      </div>
    </>,
    // 2. Family Information
    <>
      <div style={{ fontWeight: 'bold', marginTop: 8 }}>Parent/Guardian #1</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input className="form-control" style={{ flex: 1 }} name="parent1FullName" value={form.parent1FullName} onChange={handleChange} placeholder="Full Name" required />
        <input className="form-control" style={{ flex: 1 }} name="parent1Relationship" value={form.parent1Relationship} onChange={handleChange} placeholder="Relationship to Student" required />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input className="form-control" style={{ flex: 1 }} name="parent1Email" value={form.parent1Email} onChange={handleChange} placeholder="Email Address" required />
        <input className="form-control" style={{ flex: 1 }} name="parent1Phone" value={form.parent1Phone} onChange={handleChange} placeholder="Phone Number" required />
      </div>
      <input className="form-control" style={{ marginTop: 4 }} name="parent1Address" value={form.parent1Address} onChange={handleChange} placeholder="Home Address" required />
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input className="form-control" style={{ flex: 1 }} name="parent1Occupation" value={form.parent1Occupation} onChange={handleChange} placeholder="Occupation" />
        <input className="form-control" style={{ flex: 1 }} name="parent1Employer" value={form.parent1Employer} onChange={handleChange} placeholder="Employer" />
      </div>
      <div style={{ fontWeight: 'bold', marginTop: 16 }}>Parent/Guardian #2 (optional)</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input className="form-control" style={{ flex: 1 }} name="parent2FullName" value={form.parent2FullName} onChange={handleChange} placeholder="Full Name" />
        <input className="form-control" style={{ flex: 1 }} name="parent2Relationship" value={form.parent2Relationship} onChange={handleChange} placeholder="Relationship to Student" />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input className="form-control" style={{ flex: 1 }} name="parent2Email" value={form.parent2Email} onChange={handleChange} placeholder="Email Address" />
        <input className="form-control" style={{ flex: 1 }} name="parent2Phone" value={form.parent2Phone} onChange={handleChange} placeholder="Phone Number" />
      </div>
      <input className="form-control" style={{ marginTop: 4 }} name="parent2Address" value={form.parent2Address} onChange={handleChange} placeholder="Home Address (if different)" />
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input className="form-control" style={{ flex: 1 }} name="parent2Occupation" value={form.parent2Occupation} onChange={handleChange} placeholder="Occupation" />
        <input className="form-control" style={{ flex: 1 }} name="parent2Employer" value={form.parent2Employer} onChange={handleChange} placeholder="Employer" />
      </div>
    </>,
    // 3. Emergency Contact Information
    <>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            name="emergencySameAsParent1" 
            checked={form.emergencySameAsParent1} 
            onChange={handleChange}
          />
          <span style={{ fontWeight: 'bold' }}>Same as Parent/Guardian #1</span>
        </label>
      </div>
      {!form.emergencySameAsParent1 && (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="form-control" style={{ flex: 1 }} name="emergencyName" value={form.emergencyName} onChange={handleChange} placeholder="Full Name" required />
            <input className="form-control" style={{ flex: 1 }} name="emergencyRelationship" value={form.emergencyRelationship} onChange={handleChange} placeholder="Relationship to Student" required />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <input className="form-control" style={{ flex: 1 }} name="emergencyPrimaryPhone" value={form.emergencyPrimaryPhone} onChange={handleChange} placeholder="Primary Phone" required />
            <input className="form-control" style={{ flex: 1 }} name="emergencyAlternatePhone" value={form.emergencyAlternatePhone} onChange={handleChange} placeholder="Alternate Phone" />
          </div>
        </>
      )}
    </>,
    // 4. Student Educational Background
    <>
      <input className="form-control" style={{ marginTop: 4 }} name="schoolName" value={form.schoolName} onChange={handleChange} placeholder="Current/Most Recent School" required />
      <input className="form-control" style={{ marginTop: 4 }} name="schoolDates" value={form.schoolDates} onChange={handleChange} placeholder="Dates Attended" required />
      <input className="form-control" style={{ marginTop: 4 }} name="schoolReason" value={form.schoolReason} onChange={handleChange} placeholder="Reason for Leaving or Seeking Transfer (optional)" />
    </>,
    // 5. Academic & Personal Interests
    <>
      <textarea className="form-control" style={{ marginTop: 4 }} name="academicStrengths" value={form.academicStrengths} onChange={handleChange} placeholder="Please describe the applicant's academic strengths and areas for growth" rows={2} />
      <textarea className="form-control" style={{ marginTop: 4 }} name="hobbies" value={form.hobbies} onChange={handleChange} placeholder="What are the applicant's hobbies, interests, or extracurricular activities?" rows={2} />
      <textarea className="form-control" style={{ marginTop: 4 }} name="interestInDual" value={form.interestInDual} onChange={handleChange} placeholder="Why is the applicant interested in enrolling at Dual School?" rows={2} />
    </>,
    // 6. Additional Information
    <>
      <textarea className="form-control" style={{ marginTop: 4 }} name="specialNeeds" value={form.specialNeeds} onChange={handleChange} placeholder="Please include any special learning needs, accommodations, or circumstances we should be aware of" rows={2} />
    </>,
    // 7. Technology Access
    <>
      <div style={{ marginTop: 4 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
          <input type="checkbox" name="techComputer" checked={form.techComputer === true} onChange={handleChange} />
          <span>The student has daily access to a computer.</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" name="techInternet" checked={form.techInternet === true} onChange={handleChange} />
          <span>The student has reliable internet access.</span>
        </label>
      </div>
    </>,
    // 8. Transcript Upload
    <>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <label style={{ fontWeight: 'bold', marginBottom: 8 }}>Upload Academic Records / Transcripts</label>
        <input
          type="file"
          name="transcriptFile"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          className="form-control"
          style={{ marginTop: 4, width: '100%', maxWidth: 400 }}
          multiple
          onChange={handleTranscriptUpload}
        />
        {/* Show uploaded file names */}
        {uploadedTranscripts.length > 0 && (
          <ul style={{ fontSize: 13, color: '#666', marginTop: 8, textAlign: 'left', maxWidth: 400 }}>
            {uploadedTranscripts.map((name, idx) => (
              <li key={idx}>{name}</li>
            ))}
          </ul>
        )}
        <div style={{ fontSize: 13, color: '#666', marginTop: 4, textAlign: 'left', maxWidth: 400 }}>
          You may select and upload multiple files. Accepted formats: PDF, JPG, PNG, DOC. You may upload scans or photos of report cards or transcripts.
        </div>
      </div>
    </>,
    // 9. Tuition Agreement & Virtual Interview
    <>
      <div style={{ background: '#f8f8f8', border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 8, textAlign: 'left' }}>
        <div><strong>Annual Tuition:</strong> $10,000 - Payment instructions and billing setup will be provided upon acceptance.</div>
        <div style={{ marginTop: 12 }}>
          <strong>UC TAG Guarantee:</strong> If a student maintains all of the duties outlined in the contract but is not admitted into one of the 6 UC TAG schools, Dual School will issue a full refund of amounts paid less the direct course costs paid on behalf of the student.
        </div>
        <div style={{ marginTop: 12 }}>
          <strong>Virtual Interview:</strong> A virtual interview with the student and at least one parent or guardian is required as part of the admissions process. This will be scheduled upon receipt of your application.
        </div>
      </div>
    </>,
    // 10. Signature & Acknowledgment
    <>
      <p style={{ fontSize: 14 }}>
        I affirm that the information submitted in this application is accurate and complete to the best of my knowledge. I understand that admission to Dual School is contingent upon the school’s review and acceptance, and that submission of this form does not guarantee enrollment.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input className="form-control" style={{ flex: 1 }} name="parentSignature" value={form.parentSignature} onChange={handleChange} placeholder="Signature of Parent/Guardian" required />
        <input className="form-control" style={{ flex: 1 }} name="parentSignatureDate" value={form.parentSignatureDate} onChange={handleChange} placeholder="Date" type="date" required />
      </div>
    </>,
  ];

  return (
    <>
      {/* <ParentApplicationNavbar step={step} totalSteps={steps.length} /> */}
      <div style={{ width: '100%', display: 'flex', background: '#fff' }}>
        {/* Left: Overview (scrollable) */}
        <aside style={{ flex: 1, background: '#fff', borderRight: '1px solid #eee', padding: '56px 48px 40px 56px', overflowY: 'auto', textAlign: 'left' }}>
          <h2 style={{ fontWeight: 'bold', marginBottom: 24, fontSize: 22, textAlign: 'left' }}>Dual School Application Overview</h2>
          <p style={{ marginBottom: 24, fontSize: 15, lineHeight: 1.5 }}>
            Let's get started! First, we need some general information about the student (the applicant). 
            If you want to apply for more than one student, you'll be able to add them later. 
            If you need assistance, please contact <strong>chelsea@edxcelerate.com</strong>.
          </p>
          <ol style={{ marginBottom: 24, fontSize: 15, textAlign: 'left' }}>
            <li style={{ marginBottom: 12 }}>
              <strong>Introductory Steps (recommended but not required):</strong>
              <ul>
                <li><strong>Website Review:</strong> Begin by reviewing <a href="https://dual-school.com" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>our website</a> that provides a high-level overview of Dual School's philosophies and academic program.</li>
                <li><strong>Schedule an Intro Call:</strong> Connect with a member of the Admissions Team for a <a href="https://dual-school.com/interest.html" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>15-minute call</a> to answer questions and discuss next steps.</li>
              </ul>
            </li>
            <li style={{ marginBottom: 12 }}>
              <strong>Application Components (required):</strong>
              <ul>
                <li><strong>Application for Admission:</strong> Fill out the information in this application on behalf of the student.</li>
                <li><strong>Unofficial Academic Records:</strong> Upload the student's unofficial transcripts from the last 2 years.</li>
                <li><strong>Virtual Interview:</strong> A virtual interview with the student and at least one parent or guardian is required as part of the admissions process. This will be scheduled upon receipt of your application.</li>
              </ul>
            </li>
            <li>
              <strong>Decision and Enrollment:</strong>
              <ul>
                <li><strong>Rolling Admissions:</strong> Dual School enrolls rising 9th and 10th graders throughout the year. Dual School also enrolls rising 11th and 12th graders throughout the year, but since it is impossible to guarantee community college enrollments, the UC admission guarantee may be limited if the student enrolls in Dual School as an 11th or 12th grader.</li>
                <li><strong>Decision Notification:</strong> Families will be formally informed about the admission decision.</li>
              </ul>
            </li>
          </ol>
        </aside>
        {/* Right: Multi-step Form (not scrollable) */}
        <main style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', overflow: 'hidden' }}>
          <div style={{ maxWidth: 700, width: '100%', padding: '0 48px' }}>
            <h2 style={{ fontWeight: 'bold', marginBottom: 24, textAlign: 'left' }}>{step + 1}. {steps[step]}</h2>
            <form onSubmit={isComplete ? undefined : (step === steps.length - 1 ? handleSubmit : handleNext)}>
              <div style={{ minHeight: 260 }}>{stepContent[step]}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
                <Button type="button" className="custom-button" style={{ width: 120, opacity: step === 0 ? 0.5 : 1 }} onClick={handleBack} disabled={isComplete || step === 0}>Back</Button>
                {step < steps.length - 1 ? (
                  <Button type="submit" className="custom-button" style={{ width: 120, fontWeight: 'bold' }}>Next</Button>
                ) : (
                  <Button type="submit" className="custom-button" style={{ width: 180, fontWeight: 'bold', fontSize: 16 }}>Submit Application</Button>
                )}
              </div>
              <div className="center-text" style={{ marginTop: 32, fontSize: 15 }}>
                Step {step + 1} of {steps.length}
              </div>
            </form>
          </div>
        </main>
    </div>
    </>
  );
};

export default ParentApplicationPage; 